import {useAgentInfo} from '@github-ui/agent-sessions/hooks/use-agent-info'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {Token} from '@primer/react'
import {clsx} from 'clsx'
import {memo} from 'react'

import {AgentTaskPillStrings} from '../../strings'
import {tokenIconSize} from '../common/avatars-token'
import {AgentTaskPopover} from './agent-task-popover'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

function isOlderThan24Hours(timestamp: string): boolean {
  const age = Date.now() - new Date(timestamp).getTime()
  return age > TWENTY_FOUR_HOURS_MS
}

/**
 * Returns false if:
 * - No state (item has no agent task)
 * - completedAt is set and older than 24 hours
 */
export function shouldShowAgentTaskPill(state: SessionState | undefined, completedAt: string | undefined): boolean {
  if (!state) return false
  if (completedAt && isOlderThan24Hours(completedAt)) return false
  return true
}

export function useShouldShowAgentTaskPill(state: SessionState | undefined, completedAt: string | undefined): boolean {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const featureEnabled = useFeatureFlag('projects_copilot_tasks_status')

  if (!featureEnabled) return false
  return shouldShowAgentTaskPill(state, completedAt)
}

interface AgentTaskPillProps {
  state: SessionState
  agentId?: number
  className?: string
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  sessionUrl?: string
  title?: string
  nwo?: string
  createdAt?: string
  completedAt?: string
}

function getDisplayLabel(state: SessionState): string {
  switch (state) {
    case 'in_progress':
    case 'queued':
      return AgentTaskPillStrings.inProgress
    case 'completed':
      return AgentTaskPillStrings.complete
    case 'failed':
    case 'timed_out':
      return AgentTaskPillStrings.failed
    case 'cancelled':
      return AgentTaskPillStrings.cancelled
    case 'waiting_for_user':
      return AgentTaskPillStrings.waiting
    default:
      return AgentTaskPillStrings.inProgress
  }
}

function AgentIcon({agentId, size}: {agentId?: number; size?: number}) {
  const iconSize = size ?? 16
  const {data: agentInfo} = useAgentInfo(agentId ?? null)

  const avatarUrl = agentInfo?.avatar_url
  const displayName = agentInfo?.display_name || AgentTaskPillStrings.defaultAgent
  const isCopilot = !agentId || displayName.toLowerCase() === 'copilot'

  // Use Copilot icon for Copilot or when no agent info available
  if (isCopilot || !avatarUrl) {
    return <CopilotIcon size={iconSize} />
  }

  // Use avatar component for third-party agents
  return <GitHubAvatar src={avatarUrl} alt={displayName} size={iconSize} />
}

export const AgentTaskPill = memo(function AgentTaskPill({
  state,
  agentId,
  className,
  size,
  sessionUrl,
  title,
  nwo,
  createdAt,
  completedAt,
}: AgentTaskPillProps) {
  const label = getDisplayLabel(state)
  const iconSize = tokenIconSize(size)
  const isInteractive = Boolean(sessionUrl || title || nwo)
  const pillClassNames = clsx('color-fg-default', isInteractive && 'cursor-pointer', className)

  const pill = sessionUrl ? (
    <Token
      as="a"
      href={sessionUrl}
      target="_blank"
      rel="noreferrer"
      leadingVisual={() => <AgentIcon agentId={agentId} size={iconSize} />}
      text={label}
      size={size}
      className={pillClassNames}
      {...testIdProps('agent-task-pill')}
    />
  ) : (
    <Token
      as="span"
      leadingVisual={() => <AgentIcon agentId={agentId} size={iconSize} />}
      text={label}
      size={size}
      className={pillClassNames}
      {...testIdProps('agent-task-pill')}
    />
  )

  return (
    <AgentTaskPopover title={title} nwo={nwo} state={state} createdAt={createdAt} completedAt={completedAt}>
      {pill}
    </AgentTaskPopover>
  )
})
